<template>
  <transition name="auth-fade">
    <div v-if="authModal.visible" class="back-confirm-overlay">
      <transition name="auth-zoom">
        <div class="modal-wrapper">
          <!-- iOS 风格关闭按钮 - 放在弹窗外侧右上角 -->
          <button class="close-button" @click="close" aria-label="关闭">
            <svg viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M10 10L26 26M26 10L10 26" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"/>
            </svg>
          </button>
          
          <div class="back-confirm-modal">
            <div class="back-confirm-blur"></div>
            <div class="back-confirm-card auth-card">
        <!-- 顶部关闭按钮（可选） -->
        
        <component :is="currentComponent"
                   ref="formRef"
                   :isLoading="isLoading"
                   :errorMessage="errorMessage"
                   :registerSuccess="registerSuccess"
                   :shouldShowTurnstile="false"
                   :logoText="modalTitle"
                   :showDescription="false"
                   :showLogoSection="false"
                   :scroll-into-view-on-focus="false"
                   @submit="handleSubmit"
                   @switch-to-register="switchToRegister"
                   @switch-to-login="switchToLogin"
                   @forgot-password="goToForgot" />

        <div class="mode-switch-row">
          <button
            type="button"
            class="mode-switch-btn"
            :class="{ active: authModal.mode === 'register' }"
            :disabled="isLoading"
            @click="handleModeButtonClick('register')"
          >
            注册
          </button>
          <button
            type="button"
            class="mode-switch-btn"
            :class="{ active: authModal.mode === 'login' }"
            :disabled="isLoading"
            @click="handleModeButtonClick('login')"
          >
            登录
          </button>
        </div>
          </div>
        </div>
        </div>
      </transition>
    </div>
  </transition>
  
  <!-- Turnstile 人机验证弹窗 -->
  <TurnstileModal
    :visible="showTurnstileModal"
    @close="closeTurnstileModal"
    @success="onTurnstileSuccess"
  />
  
  <!-- 消息弹窗 -->
  <MessageModal
    :visible="showMessageModal"
    :type="messageType"
    :title="messageTitle"
    :message="messageText"
    @close="closeMessageModal"
  />
</template>

<script setup>
import { computed, ref, watch, onMounted, onUnmounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useUserStore } from '@/store/user'
import { useAuthModalStore } from '@/store/authModal'
import PhoneLoginForm from '@/components/auth/PhoneLoginForm.vue'
import PhoneRegisterForm from '@/components/auth/PhoneRegisterForm.vue'
import TurnstileModal from '@/components/auth/TurnstileModal.vue'
import MessageModal from '@/components/auth/MessageModal.vue'
import {
  validateInviteCodeForRegister,
  buildRegisterInviteCodePayload
} from '@/constants/registerConfig'
import { getUsernameFormatError } from '@/utils/accountCredentialRules'

const router = useRouter()
const route = useRoute()
const userStore = useUserStore()
const authModal = useAuthModalStore()

const errorMessage = ref('')
const registerSuccess = ref(false)
const formRef = ref(null)
const showTurnstileModal = ref(false)
const turnstileToken = ref('')
const pendingFormData = ref(null)
const showMessageModal = ref(false)
const messageType = ref('error')
const messageTitle = ref('')
const messageText = ref('')

const isLoading = computed(() => userStore.loading)
const currentComponent = computed(() => authModal.mode === 'register' ? PhoneRegisterForm : PhoneLoginForm)
const modalTitle = computed(() => authModal.mode === 'register' ? '注册DXH的小站' : '登录到DXH的小站')

const close = () => {
  authModal.close()
  // 清除 URL 上的 auth 查询参数，但停留在当前页面
  if (route.query.auth) {
    const { auth, ...rest } = route.query
    router.replace({ path: route.path, query: { ...rest } })
  }
}

const switchToRegister = () => {
  authModal.mode = 'register'
  router.replace({ path: route.path, query: { ...route.query, auth: 'register' } })
}

const switchToLogin = () => {
  authModal.mode = 'login'
  router.replace({ path: route.path, query: { ...route.query, auth: 'login' } })
}

const handleModeButtonClick = (targetMode) => {
  if (authModal.mode === targetMode) {
    const formData = formRef.value?.formData ? { ...formRef.value.formData } : null
    if (!formData) return
    handleSubmit(formData)
    return
  }

  if (targetMode === 'register') {
    switchToRegister()
    return
  }

  switchToLogin()
}

const validateLoginForm = (formData) => {
  errorMessage.value = ''
  if (!formData.username) {
    errorMessage.value = '请输入用户账号'
    return false
  }
  if (!formData.password) {
    errorMessage.value = '请输入密码'
    return false
  }
  return true
}

const validateRegisterForm = (formData) => {
  errorMessage.value = ''
  if (!formData.username) {
    errorMessage.value = '请输入账号'
    return false
  }
  const trimmedUsername = formData.username.trim()
  if (trimmedUsername !== formData.username) {
    errorMessage.value = '账号首尾不能包含空格'
    return false
  }
  if (formData.username.length > 20) {
    errorMessage.value = '账号长度不能超过20位'
    return false
  }
  const usernameFormatError = getUsernameFormatError(formData.username)
  if (usernameFormatError) {
    errorMessage.value = usernameFormatError
    return false
  }
  if (/^\d/.test(formData.username)) {
    errorMessage.value = '账号不能以数字开头'
    return false
  }
  if (!formData.password) {
    errorMessage.value = '请输入密码'
    return false
  }
  if (formData.password.length > 20) {
    errorMessage.value = '密码长度不能超过20位'
    return false
  }
  if (!formData.confirmPassword) {
    errorMessage.value = '请确认密码'
    return false
  }
  if (formData.password !== formData.confirmPassword) {
    errorMessage.value = '两次输入的密码不一致'
    return false
  }
  const trimmedInviteCode = validateInviteCodeForRegister(formData.inviteCode)
  if (!trimmedInviteCode.ok) {
    errorMessage.value = trimmedInviteCode.message
    return false
  }
  return true
}

const handleSubmit = async (formData) => {
  if (authModal.mode === 'login') {
    if (!validateLoginForm(formData)) {
      showMessage('error', '登录失败', errorMessage.value)
      return
    }
    errorMessage.value = ''
    try {
      await userStore.userLogin({
        userAccount: formData.username,
        userPassword: formData.password,
        rememberMe: true
      })
      await userStore.fetchCurrentUser()

      // 处理跳转逻辑：优先 sessionStorage.redirectAfterLogin，其次 query.redirect，最后 localStorage.returnUrl
      const redirectAfterLogin = sessionStorage.getItem('redirectAfterLogin')
      const returnUrl = localStorage.getItem('returnUrl')
      const redirectPath = redirectAfterLogin || route.query.redirect || returnUrl || '/user'
      
      // 清除 redirectAfterLogin
      if (redirectAfterLogin) {
        sessionStorage.removeItem('redirectAfterLogin')
      }
      
      if (returnUrl && /^https?:\/\//.test(returnUrl)) {
        localStorage.removeItem('returnUrl')
        window.location.href = returnUrl
      } else {
        localStorage.removeItem('returnUrl')
        // 如果当前不在重定向目的地，跳转到目的地，否则仅关闭弹窗
        if (redirectPath && redirectPath !== route.fullPath) {
          router.push(String(redirectPath))
        }
      }
      close()
    } catch (e) {
      showMessage('error', '登录失败', e?.message || '登录失败，请稍后重试')
    }
  } else {
    // register - 显示 Turnstile 弹窗
    if (!validateRegisterForm(formData)) {
      showMessage('error', '注册失败', errorMessage.value)
      return
    }
    pendingFormData.value = formData
    showTurnstileModal.value = true
  }
}

const closeTurnstileModal = () => {
  showTurnstileModal.value = false
}

const closeMessageModal = () => {
  showMessageModal.value = false
  errorMessage.value = ''
}

const showMessage = (type, title, text) => {
  messageType.value = type
  messageTitle.value = title
  messageText.value = text
  showMessageModal.value = true
}

const onTurnstileSuccess = async (token) => {
  turnstileToken.value = token
  
  if (!pendingFormData.value) {
    showMessage('error', '注册失败', '表单数据丢失，请重新填写')
    return
  }
  
  errorMessage.value = ''
  try {
    // 直接使用接口进行注册
    const response = await fetch(`${import.meta.env.VITE_APP_API_BASE_URL}/api/user/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        userAccount: pendingFormData.value.username,
        userPassword: pendingFormData.value.password,
        checkPassword: pendingFormData.value.confirmPassword,
        ...(buildRegisterInviteCodePayload(pendingFormData.value.inviteCode) != null
          ? { inviteCode: buildRegisterInviteCodePayload(pendingFormData.value.inviteCode) }
          : {}),
        turnstileToken: token
      })
    })
    const result = await response.json()
    if (result.code === 0) {
      registerSuccess.value = true
      showMessage('success', '注册成功', '注册成功！正在跳转到登录页面...')
      // 3 秒后自动切换到登录
      setTimeout(() => {
        closeMessageModal()
        switchToLogin()
      }, 3000)
    } else {
      showMessage('error', '注册失败', result.message || '注册失败，请稍后重试')
      formRef.value?.resetForm?.()
    }
  } catch (e) {
    showMessage('error', '注册失败', '注册失败，请稍后重试')
    formRef.value?.resetForm?.()
  } finally {
    pendingFormData.value = null
    turnstileToken.value = ''
  }
}

const goToForgot = () => {
  close()
  router.push('/forgot-password')
}

// 监听 ESC 关闭
const onKeydown = (e) => {
  if (e.key === 'Escape' && authModal.visible) close()
}

onMounted(() => {
  window.addEventListener('keydown', onKeydown)
})

onUnmounted(() => {
  window.removeEventListener('keydown', onKeydown)
})

// 外部根据 ?auth=login|register 打开
watch(
  () => route.query.auth,
  (val) => {
    if (val === 'login' || val === 'register') {
      authModal.open(val)
    }
  },
  { immediate: true }
)
</script>

<style scoped>
/* 复用 PinDou.vue 的毛玻璃遮罩与卡片视觉（略作提炼） */
.back-confirm-overlay {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.35);
  z-index: 15000;
  backdrop-filter: blur(5px);
  -webkit-backdrop-filter: blur(10px);
}

/* 弹窗包装器 - 用于定位关闭按钮 */
.modal-wrapper {
  position: relative;
  display: inline-block;
}

/* iOS 风格关闭按钮 */
.close-button {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  background: rgba(0, 0, 0, 0.1);
  mix-blend-mode: plus-darker;
  border-radius: 100px;
  border: none;
  cursor: pointer;
  z-index: 1;
  transition: all 0.2s ease;
  outline: none;
}

.close-button svg {
  width: 22px;
  height: 22px;
  color: #999999;
  mix-blend-mode: plus-darker;
}

.close-button:hover {
  background: rgba(0, 0, 0, 0.14);
  transform: scale(1.05);
}

.close-button:active {
  background: rgba(0, 0, 0, 0.14);
  transform: scale(0.95);
}

.back-confirm-modal {
  --auth-secondary-surface: rgba(0, 0, 0, 0.14);
  position: relative;
  display: inline-block;
  width: 236px;
  min-width: 230px;
  max-width: min(302px, 83vw);
  height: auto;
  padding: 0;
  border-radius: 26px;
  overflow: visible;
  isolation: isolate;
  background-color: transparent;
}


.back-confirm-blur {
  position: absolute;
  inset: -40px;
  background: rgba(245, 245, 245, 0);
  border-radius: 26px;
  z-index: 0;
}

.back-confirm-card {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  box-sizing: border-box;
  background: rgba(255, 255, 255, 0.85);
  border-radius: 26px;
  padding: 16px 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
  overflow: visible;
}

/* 让 auth 卡片作为定位容器，内部元素用百分比定位 */
.auth-card {
  position: relative;
  overflow: visible;
  border-radius: 26px;
  gap : 8px !important;
}

.mode-switch-row {
  width: 80%;
  margin: 10px auto 0;
  display: flex;
  align-items: center;
  gap: 12px;
}

.mode-switch-btn {
  flex: 1;
  min-height: 44px;
  border: none;
  border-radius: 999px;
  background: var(--auth-secondary-surface);
  color: rgba(0, 0, 0, 0.72);
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.mode-switch-btn.active {
  background: rgba(0, 136, 255, 1);
  color: #ffffff;
  box-shadow: 0 6px 16px rgba(0, 122, 255, 0.35);
}

.mode-switch-btn:not(.active):hover {
  background: rgba(0, 0, 0, 0.2);
  color: rgba(0, 0, 0, 0.88);
}

.mode-switch-btn.active:hover,
.mode-switch-btn.active:focus,
.mode-switch-btn.active:focus-visible,
.mode-switch-btn.active:active {
  background: rgba(0, 136, 255, 1);
}

.mode-switch-btn:disabled {
  cursor: not-allowed;
  opacity: 0.75;
}

/* 让表单容器采用文档流，内容自然撑开卡片尺寸（PC/移动一致策略） */
:deep(.phone-form-container) {
  position: static !important;
  inset: auto !important;
  margin: 0 !important;
  padding: 0 !important;
  box-shadow: none !important;
  background: transparent !important;
  width: 100% !important;
  max-width: 360px !important;
  height: auto !important;
  max-height: none !important;
  aspect-ratio: auto !important;
}

/* 统一关闭底部品牌区，避免占高 */
:deep(.phone-form-container .logo-section) {
  display: none !important;
}

/* 让标题、表单、按钮在文档流中自然排列 */
:deep(.phone-form-container .main-logo-container),
:deep(.phone-form-container .login-form),
:deep(.phone-form-container .login-action-container),
:deep(.phone-form-container .register-form),
:deep(.phone-form-container .register-action-container) {
  position: static !important;
  top: auto !important;
  left: auto !important;
  transform: none !important;
  width: 100% !important;
}

/* 统一内边距与间距 */
:deep(.phone-form-container .main-logo-container) {
  margin: 8px 0 4px 0 !important;
  text-align: center !important;
}
:deep(.phone-form-container .login-form),
:deep(.phone-form-container .register-form) {

  width: 80% !important;
  margin: 30px auto !important;
}
:deep(.phone-form-container .login-action-container),
:deep(.phone-form-container .register-action-container) {
  display: none !important;
}

:deep(.phone-form-container .register-link),
:deep(.phone-form-container .login-link) {
  display: none !important;
}

/* 调整标题字号并清除上移偏移，避免被挤出卡片 */
:deep(.phone-form-container .login-title) {
  position: static !important;
  top: auto !important;
  transform: none !important;
  margin: 0 0 22px 0 !important;
  font-size: 30px !important;
  line-height: 1.05 !important;
  
}

:deep(.phone-form-container .forgot-password) {
    
    margin:  0px !important;
    height: 0px;

}

:deep(.phone-form-container .register-form ) {
    
    width: 80% !important;
    margin: 30 auto !important;


}


/* 给标题容器一些安全上边距 */
:deep(.phone-form-container .main-logo-container) {
  padding-top: 6px !important;
}

/* 说明文案的间距微调，防止压住标题 */
:deep(.phone-form-container .feature-description) {
  margin: 6px 0 8px 0 !important;
  transform: none !important;
}

.modal-close {
  position: absolute;
  top: 6px;
  right: 10px;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: rgba(0,0,0,0.06);
  color: #333;
  font-size: 18px;
  line-height: 28px;
  text-align: center;
  cursor: pointer;
}

/* 适配 PhoneLoginForm/PhoneRegisterForm 的默认外边距 */
:deep(.phone-form-container) {
  margin: 0 !important;
  box-shadow: none !important;
  background: transparent !important;
}
/* 弹层淡入淡出 */
.auth-fade-enter-active,
.auth-fade-leave-active {
  transition: opacity 0.18s ease;
}
.auth-fade-enter-from,
.auth-fade-leave-to {
  opacity: 0;
}

/* 卡片轻微缩放与上浮 */
.auth-zoom-enter-active,
.auth-zoom-leave-active {
  transition: transform 0.18s ease, opacity 0.18s ease;
  will-change: transform, opacity;
}
.auth-zoom-enter-from,
.auth-zoom-leave-to {
  transform: translateY(6px) scale(0.98);
  opacity: 0;
}

/* 关闭按钮动画 */
.auth-zoom-enter-active .close-button,
.auth-zoom-leave-active .close-button {
  transition: opacity 0.18s ease, transform 0.18s ease;
}

.auth-zoom-enter-from .close-button,
.auth-zoom-leave-to .close-button {
  opacity: 0;
  transform: scale(0.8);
}

/* ===== iOS 风格输入框（玻璃质感） ===== */
:deep(.login-form .input-group),
:deep(.register-form .input-group) {

}

:deep(.login-input),
:deep(.register-input) {
  width: 100% !important;
  min-width: 0 !important;
  display: block !important;
  height: 48px !important;
  padding: 0 14px !important;
  border-radius: 14px !important;
  border: 1px solid rgba(0,0,0,0.12) !important;
  background: rgba(255, 255, 255, 0.60) !important;
  backdrop-filter: blur(16px) !important;
  -webkit-backdrop-filter: blur(16px) !important;
  box-shadow:
    inset 0 0 0 0.5px rgba(255,255,255,0.7),
    0 1px 2px rgba(0,0,0,0.06) !important;
  color: #111 !important;
  font-size: 15px !important;
  font-weight: 600 !important;
  letter-spacing: 0 !important;
  outline: none !important;
  transition: border-color .18s ease, box-shadow .18s ease, background .18s ease;
  /* 去掉旧的下划线风格 */
  border-bottom: none !important;
}

/* placeholder 颜色 */
:deep(.login-input::placeholder),
:deep(.register-input::placeholder) {
  color: rgba(60, 60, 67, 0.6) !important;
  font-weight: 500 !important;
}

/* 焦点态：iOS 蓝色描边+更清晰玻璃 */
:deep(.login-input:focus),
:deep(.register-input:focus) {
  border-color: rgba(0, 122, 255, 0.65) !important;
  box-shadow:
    0 0 0 2px rgba(0, 122, 255, 0.18),
    inset 0 0 0 0.5px rgba(255,255,255,0.85) !important;
  background: rgba(255, 255, 255, 0.72) !important;
}

/* 密码框去掉夸张的字距 */
:deep(.login-input[type="password"]),
:deep(.register-input[type="password"]) {
  letter-spacing: 0.3px !important;
}

/* iOS Safari 自动填充兼容 - 在分组风格下保持透明，不要在输入内再铺一层底色 */
:deep(.login-input:-webkit-autofill),
:deep(.register-input:-webkit-autofill),
:deep(.login-input:-webkit-autofill:hover),
:deep(.register-input:-webkit-autofill:hover),
:deep(.login-input:-webkit-autofill:focus),
:deep(.register-input:-webkit-autofill:focus) {
  -webkit-box-shadow: 0 0 0 1000px transparent inset !important;
  -webkit-text-fill-color: #111 !important;
  border: none !important;
  box-shadow: none !important;
  background: transparent !important;
}

/* ===== iOS 分组输入组（Group）风格 ===== */
:deep(.login-form),
:deep(.register-form) {
  background: rgba(255, 255, 255, 0.60) !important;
  backdrop-filter: blur(16px) !important;
  -webkit-backdrop-filter: blur(16px) !important;

  border-radius: 10px !important;
  padding: 0px 0px !important;

  

}

/* 每一项为一行，之间使用淡灰分隔线 */
:deep(.login-form .input-group),
:deep(.register-form .input-group) {

  /* 统一左右内边距，保证输入文字与占位符有同等左边距 */
  padding: 4px 12px !important;

}

:deep(.login-form .input-group + .input-group),
:deep(.register-form .input-group + .input-group) {
  border-top: 1px solid rgba(67, 65, 60, 0.12) !important;
  width: 100%;

  
}

/* 输入框贴合分组容器，不再单独展示边框与背景 */
:deep(.login-input),
:deep(.register-input) {
  width: 100% !important;
  min-width: 0 !important;
  height: 44px !important;
  line-height: 44px !important;
  padding: 0 !important;
  margin: 0 !important;
  background: transparent !important;
  background-color: transparent !important;
  border: none !important;
  box-shadow: none !important;
  outline: none !important;
  color: #111 !important;
  font-size: 15px !important;
  font-weight: 600 !important;
  /* 🍎 重置 iOS Safari 原生 input 样式（默认会有内嵌阴影显得更暗） */
  -webkit-appearance: none !important;
  -moz-appearance: none !important;
  appearance: none !important;
  -webkit-tap-highlight-color: transparent !important;
}

/* 占位符颜色更柔和 */
:deep(.login-input::placeholder),
:deep(.register-input::placeholder) {
  color: rgba(60, 60, 67, 0.45) !important;
  font-weight: 500 !important;
  transform: none !important;
}

/* 焦点所在的那一行，稍微提亮背景以示聚焦 */
:deep(.login-form .input-group:focus-within),
:deep(.register-form .input-group:focus-within) {
  background: rgba(255, 255, 255, 0.50) !important;
  border-radius: 12px !important;
}

/* 去掉“忘记密码？”行的占位空间（该行在主站设计中隐藏） */
:deep(.phone-form-container .forgot-password) {
  margin: 0 !important;
  padding: 0 !important;
  height: 0 !important;
  overflow: hidden !important;
}
/* 彻底关闭输入框自身的蓝边/发光，保留行级 focus-within 高亮 */
:deep(.login-input),
:deep(.register-input) {
  outline: none !important;
  border: none !important;
  box-shadow: none !important;
  -webkit-appearance: none !important;
  appearance: none !important;
  background: transparent !important;
  background-clip: padding-box !important;
  transform: none !important;
}

:deep(.login-input:focus),
:deep(.register-input:focus),
:deep(.login-input:focus-visible),
:deep(.register-input:focus-visible),
:deep(.login-input:hover),
:deep(.register-input:hover),
:deep(.login-input:active),
:deep(.register-input:active),
:deep(.login-input:not(:placeholder-shown)),
:deep(.register-input:not(:placeholder-shown)) {
  outline: none !important;
  border: none !important;
  border-color: transparent !important;
  box-shadow: none !important;
  background: transparent !important;
  transform: none !important;
}

/* 进一步覆盖 Chromium 的内部 autofill 选择态 */
:deep(input:-internal-autofill-selected) {
  background-color: transparent !important;
  box-shadow: 0 0 0 1000px transparent inset !important;
}
/* 居中显示弹窗（PC/移动一致） */
.back-confirm-overlay {
  align-items: center !important;
  justify-content: center !important;
}
.back-confirm-modal {
  margin-top: 0 !important;
}

/* ===== Final Visual Alignment (follow design reference) ===== */
.back-confirm-modal {
  border-radius: 26px !important;
}

.back-confirm-blur,
.back-confirm-card,
.auth-card {
  border-radius: 26px !important;
}

.back-confirm-card {
  padding: 14px !important;
}

.mode-switch-row {
  width: 100%;
  margin: 4px auto 0 !important;
}

:deep(.phone-form-container .main-logo-container) {
  margin: 0 0 8px 0 !important;
  padding-top: 0 !important;
}

:deep(.phone-form-container .login-title) {
  margin: 5px 0 30px !important;
  font-size: 17px !important;
  font-weight: 700 !important;
  line-height: 22px !important;
  letter-spacing: -0.43px !important;
  font-family: "SF Pro Text", "SF Pro Display", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif !important;
}

:deep(.phone-form-container .login-form),
:deep(.phone-form-container .register-form) {
  width: 100% !important;
  margin: 12px auto 16px !important;
  padding: 0 !important;
  background: transparent !important;
  border-radius: 26px !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
}

:deep(.login-form .input-group),
:deep(.register-form .input-group) {
  margin: 8px 0 !important;
  padding: 0 14px !important;
  border: 1px solid rgba(60, 60, 67, 0.18) !important;
  border-radius: 18px !important;
  background: rgba(255, 255, 255, 0.72) !important;
}

:deep(.login-form .input-group + .input-group),
:deep(.register-form .input-group + .input-group) {
  border-top: none !important;
}

:deep(.login-input),
:deep(.register-input) {
  height: 44px !important;
  line-height: 44px !important;
  padding: 0 !important;
  margin: 0 !important;
  border: none !important;
  border-radius: 16px !important;
  background: transparent !important;
  box-shadow: none !important;
}

/* ===== Auth form merged field group (login + register share same visual style) ===== */
:deep(.phone-form-container .login-form),
:deep(.phone-form-container .register-form) {
  margin: 12px auto 16px !important;
  padding: 0 !important;
  background: transparent !important;
  border: none !important;
  border-radius: 26px !important;
  overflow: hidden !important;
  position: relative !important;
}

:deep(.phone-form-container .login-form)::before,
:deep(.phone-form-container .register-form)::before {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: 26px;
  background: var(--auth-secondary-surface);
  border: none;
  backdrop-filter: none;
  -webkit-backdrop-filter: none;
  pointer-events: none;
  z-index: 0;
}

/* Auth forms should be opaque (non-transparent) */
:deep(.phone-form-container .login-form),
:deep(.phone-form-container .register-form) {
  background: rgb(219, 219, 219) !important;
}

:deep(.phone-form-container .login-form)::before,
:deep(.phone-form-container .register-form)::before {
  content: none !important;
  display: none !important;
}

:deep(.login-form .input-group),
:deep(.register-form .input-group) {
  margin: 0 !important;
  height: 52px !important;
  padding: 0 16px !important;
  border: none !important;
  border-radius: 0 !important;
  background: transparent !important;
  display: flex !important;
  align-items: center !important;
  position: relative !important;
  z-index: 1 !important;
}

:deep(.login-form .input-group + .input-group),
:deep(.register-form .input-group + .input-group) {
  border-top: none !important;
}

:deep(.login-form .input-group + .input-group)::before,
:deep(.register-form .input-group + .input-group)::before {
  content: "";
  position: absolute;
  left: 16px;
  right: 16px;
  top: 0;
  border-top: 0.5px solid rgba(60, 60, 67, 0.2);
}

:deep(.login-form .login-input),
:deep(.register-form .register-input) {
  width: 100% !important;
  height: 52px !important;
  line-height: 52px !important;
  padding: 0 !important;
  border-radius: 0 !important;
  background: transparent !important;
  background-color: transparent !important;
  box-shadow: none !important;
  -webkit-appearance: none !important;
  appearance: none !important;
  background-image: none !important;
  transition: none !important;
  opacity: 1 !important;
  caret-color: #111 !important;
  -webkit-text-fill-color: #111 !important;
  position: relative !important;
  z-index: 1 !important;
}

:deep(.login-form .input-group:focus-within),
:deep(.register-form .input-group:focus-within),
:deep(.login-form .input-group:hover),
:deep(.register-form .input-group:hover),
:deep(.login-form .input-group:active),
:deep(.register-form .input-group:active) {
  background: transparent !important;
  border-radius: 0 !important;
}

:deep(.login-form .login-input:focus),
:deep(.register-form .register-input:focus),
:deep(.login-form .login-input:focus-visible),
:deep(.register-form .register-input:focus-visible),
:deep(.login-form .login-input:hover),
:deep(.register-form .register-input:hover),
:deep(.login-form .login-input:active),
:deep(.register-form .register-input:active),
:deep(.login-form .login-input:not(:placeholder-shown)),
:deep(.register-form .register-input:not(:placeholder-shown)) {
  background: transparent !important;
  background-color: transparent !important;
  box-shadow: none !important;
}

:deep(.login-form .login-input:-webkit-autofill),
:deep(.register-form .register-input:-webkit-autofill),
:deep(.login-form .login-input:-webkit-autofill:hover),
:deep(.register-form .register-input:-webkit-autofill:hover),
:deep(.login-form .login-input:-webkit-autofill:focus),
:deep(.register-form .register-input:-webkit-autofill:focus) {
  background: transparent !important;
  background-color: transparent !important;
  -webkit-text-fill-color: #111 !important;
  -webkit-box-shadow: 0 0 0 1000px transparent inset !important;
}

/* Final lock: ensure login/register input area never paints an extra background layer */
.back-confirm-modal :deep(.phone-form-container .login-form .input-group),
.back-confirm-modal :deep(.phone-form-container .register-form .input-group),
.back-confirm-modal :deep(.phone-form-container .login-form .login-input),
.back-confirm-modal :deep(.phone-form-container .register-form .register-input),
.back-confirm-modal :deep(.phone-form-container .login-form .login-input:focus),
.back-confirm-modal :deep(.phone-form-container .register-form .register-input:focus),
.back-confirm-modal :deep(.phone-form-container .login-form .login-input:focus-visible),
.back-confirm-modal :deep(.phone-form-container .register-form .register-input:focus-visible),
.back-confirm-modal :deep(.phone-form-container .login-form .login-input:hover),
.back-confirm-modal :deep(.phone-form-container .register-form .register-input:hover),
.back-confirm-modal :deep(.phone-form-container .login-form .login-input:active),
.back-confirm-modal :deep(.phone-form-container .register-form .register-input:active),
.back-confirm-modal :deep(.phone-form-container .login-form .login-input:not(:placeholder-shown)),
.back-confirm-modal :deep(.phone-form-container .register-form .register-input:not(:placeholder-shown)),
.back-confirm-modal :deep(.phone-form-container .login-form .input-group:focus-within),
.back-confirm-modal :deep(.phone-form-container .register-form .input-group:focus-within),
.back-confirm-modal :deep(.phone-form-container .login-form .input-group:hover),
.back-confirm-modal :deep(.phone-form-container .register-form .input-group:hover),
.back-confirm-modal :deep(.phone-form-container .login-form .input-group:active),
.back-confirm-modal :deep(.phone-form-container .register-form .input-group:active) {
  background: transparent !important;
  background-color: transparent !important;
  box-shadow: none !important;
}

/* Stabilize register password fields to prevent caret-blink repaint flicker */
.back-confirm-modal :deep(.phone-form-container .register-form .register-input[type="password"]),
.back-confirm-modal :deep(.phone-form-container .register-form .register-input[type="password"]:focus),
.back-confirm-modal :deep(.phone-form-container .register-form .register-input[type="password"]:active) {
  background-color: rgba(255, 255, 255, 0.001) !important;
  -webkit-background-clip: padding-box !important;
  transform: translateZ(0) !important;
  backface-visibility: hidden !important;
  -webkit-text-fill-color: #111 !important;
  caret-color: #111 !important;
}

/* 🍎 iOS Safari 特定修复：彻底去除 input 默认的灰底/凹陷效果 */
@supports (-webkit-touch-callout: none) {
  /* 关键修复：移除表单容器的 backdrop-filter，避免 iOS 上 input 子元素被错误合成出渐变底色 */
  :deep(.login-form),
  :deep(.register-form) {
    backdrop-filter: none !important;
    -webkit-backdrop-filter: none !important;
    background: rgba(255, 255, 255, 0.85) !important;
  }

  /* 🔥 关键：让 input 自己有显式的浅色背景，而不是依赖透明继承 input-group。
     这样 content 区域和 padding 区域是同一个背景，不会有视觉割裂 */
  :deep(.login-input),
  :deep(.register-input) {
    /* 关键：给 input 一个明确的纯白底色，覆盖 content/padding 整个区域 */
    background: #ffffff !important;
    background-color: #ffffff !important;
    background-image: none !important;
    /* 用 padding-box 让背景只画在 padding 内（不蔓延到 border） */
    background-clip: padding-box !important;
    -webkit-background-clip: padding-box !important;
    background-origin: padding-box !important;
    -webkit-background-origin: padding-box !important;
    /* 重置 iOS UIKit 原生渲染 */
    -webkit-appearance: none !important;
    appearance: none !important;
    box-shadow: none !important;
    -webkit-box-shadow: none !important;
    -webkit-text-fill-color: #111 !important;
    -webkit-tap-highlight-color: rgba(0, 0, 0, 0) !important;
    /* transition 只作用于颜色/边框，绝不让 background 过渡 */
    transition: color 0.18s ease, border-color 0.18s ease !important;
    -webkit-transition: color 0.18s ease, border-color 0.18s ease !important;
    transform: none !important;
    -webkit-transform: none !important;
    filter: none !important;
    -webkit-filter: none !important;
  }

  /* input-group 也用同样的纯白底，避免父子背景层不同造成的视觉差 */
  :deep(.login-form .input-group),
  :deep(.register-form .input-group) {
    background: #ffffff !important;
    background-color: #ffffff !important;
  }

  /* login-form/register-form 也改纯白，整个区域统一 */
  :deep(.login-form),
  :deep(.register-form) {
    background: #ffffff !important;
    background-color: #ffffff !important;
  }

  /* focus 状态 */
  :deep(.login-input:focus),
  :deep(.register-input:focus) {
    background: #ffffff !important;
    background-color: #ffffff !important;
    transform: none !important;
    -webkit-transform: none !important;
    -webkit-box-shadow: none !important;
    box-shadow: none !important;
  }

  /* autofill 状态 */
  :deep(.login-input:-webkit-autofill),
  :deep(.register-input:-webkit-autofill),
  :deep(.login-input:-internal-autofill-selected),
  :deep(.register-input:-internal-autofill-selected) {
    -webkit-box-shadow: 0 0 0 1000px #ffffff inset !important;
    -webkit-text-fill-color: #111 !important;
    background-color: #ffffff !important;
    transition: color 0.18s ease, border-color 0.18s ease !important;
    -webkit-transition: color 0.18s ease, border-color 0.18s ease !important;
  }
}

.back-confirm-modal :deep(.phone-form-container .register-form .register-input[type="password"]:focus::placeholder) {
  opacity: 0 !important;
  transition: none !important;
}

/* 响应式：移动端调整关闭按钮位置 */
@media (max-width: 640px) {
  .close-button {
    /* 移动端和桌面端使用相同尺寸 */
    top: 8px;
    right: 8px;
  }
}
</style>

